import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

import { useFiltersContext } from "./Filters";

import { RatingsContextProps, RatingsContextValues } from "../types/contexts";
import { SortColumns } from "../types/ratings";

import { IUmpireRatingAverage } from "../../../../../../types/api";
import { SortOrders } from "../../../../../../types/shared";

const RatingsContext = createContext<RatingsContextValues | null>(null);

export const useRatingsContext = () => {
    const context = useContext(RatingsContext);
    if (!context) {
        throw new Error(
            "useRatingsContext must be used within a RatingsProvider",
        );
    }
    return context;
};

export const RatingsProvider: React.FC<RatingsContextProps> = props => {
    const { children, initialRatings } = props;
    const { filters } = useFiltersContext();
    const [ratings, setRatings] = useState<IUmpireRatingAverage[]>([]);

    useEffect(() => {
        setRatings(initialRatings);
    }, [initialRatings]);

    const [sortColumn, setSortColumn] = useState<SortColumns>("name");
    const [sortOrder, setSortOrder] = useState<SortOrders>("ascending");

    const filteredRatings = useMemo(() => {
        return ratings
            .filter(r =>
                filters.umpire
                    ? r.umpire.name
                          ?.toLowerCase()
                          .includes(filters.umpire.toLowerCase())
                    : true,
            )
            .filter(r =>
                filters.attitude && r.attitude
                    ? r.attitude <= filters.attitude
                    : true,
            )
            .filter(r =>
                filters.consistency && r.consistency
                    ? r.consistency <= filters.consistency
                    : true,
            )
            .filter(r =>
                filters.effort && r.effort ? r.effort <= filters.effort : true,
            )
            .filter(r =>
                filters.overall && r.overall
                    ? r.overall <= filters.overall
                    : true,
            )
            .filter(r => (filters.count ? r.count >= filters.count : true));
    }, [ratings, filters]);

    const sortedRatings = useMemo(() => {
        let aValue: any;
        let bValue: any;

        return [...filteredRatings].sort((a, b) => {
            switch (sortColumn) {
                case "name":
                    aValue = a.umpire.name;
                    bValue = b.umpire.name;
                    break;
                case "attitude":
                    aValue = a.attitude;
                    bValue = b.attitude;
                    break;
                case "consistency":
                    aValue = a.consistency;
                    bValue = b.consistency;
                    break;
                case "effort":
                    aValue = a.effort;
                    bValue = b.effort;
                    break;
                case "overall":
                    aValue = a.overall;
                    bValue = b.overall;
                    break;
                case "count":
                    aValue = a.count;
                    bValue = b.count;
                    break;
            }

            if (sortOrder === "ascending") {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });
    }, [filteredRatings, sortColumn, sortOrder]);

    const contextValue = useMemo(
        () => ({
            ratings: sortedRatings,
            setRatings,
            totalItems: sortedRatings.length,
            sortColumn,
            setSortColumn,
            sortOrder,
            setSortOrder,
        }),
        [sortedRatings],
    );

    return (
        <RatingsContext.Provider value={contextValue}>
            {children}
        </RatingsContext.Provider>
    );
};
